import { useState, useCallback, useMemo } from 'react';
import { Box, CircularProgress, Paper } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Search } from '@mui/icons-material';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { SCUIContent } from '../../atomic/organisms';
import { SCUIBreadCrumb, SCUICustomDataGrid } from '../../atomic/molecules';
import { PageUrl } from '../../types/enums/pageUrl-enums';
import { SCUIButton, SCUILoadingLinear, SCUINoRecordComponent } from '../../atomic/atoms';
import { SearchDrawer } from './components';
import airdropShipment from '../../assets/icons/svg/pageIcons/airdropShipment.svg';
import CargoCompanyDistributionAreasCommand from '../../services/command/cargoCompanyDistributionAreas/CargoCompanyDistributionAreasCommand.services';
import { downloadExcel } from '../../utils/excelFileHelper';
import TimeHelper from '../../utils/time';
import Swal from 'sweetalert2';
import { CCDA, ccdaCls } from './cargoCompanyDistributionAreas.classes';

// Constants
const DEFAULT_PAGE_SIZE = 20;
const EXCEL_FOLDER_NAME = 'CargoCompanyDistributionAreas';
const ALL_OPTION_ID = -1;

interface GridRow {
    id: number;
    shippingCompanyName: string;
    cityName: string;
    countyName: string;
    neighborhoodName: string;
    distributionTypeName: string;
    dayCount: number;
    weekDays: string;
    changedOn: string;
}

interface FilterParams {
    cargoId: number;
    cityId: number;
    countyId: number;
    distributionTypeId: number;
}

interface ApiPayload {
    cargoId: number;
    cityId: number;
    countyId: number;
    distributionTypeId: number;
    page: number;
    pageSize: number;
}

// Helper function to transform API response items to GridRow
const transformApiItemsToGridRows = (items: any[]): any[] => {
    if (!Array.isArray(items)) return [];
    return items.map((item: any) => ({
        id: item.id || 0,
        shippingCompanyName: item.shippingCompanyName || '-',
        cityName: item.cityName || '-',
        countyName: item.countyName || '-',
        neighborhoodName: item.neighborhoodName || '-',
        distributionTypeName: item.distributionTypeName || '-',
        dayCount: item.dayCount || 0,
        weekDays: item.weekDays || '-',
        changedOn: item.changedOn || '',
    }));
};

// Helper function to build API payload from filter params
const buildApiPayload = (filterParams: FilterParams, page: number, pageSize: number): ApiPayload => ({
    cargoId: filterParams.cargoId,
    cityId: filterParams.cityId === ALL_OPTION_ID ? 0 : filterParams.cityId,
    countyId: filterParams.countyId === ALL_OPTION_ID ? -1 : filterParams.countyId,
    distributionTypeId: filterParams.distributionTypeId === ALL_OPTION_ID ? 0 : filterParams.distributionTypeId,
    page,
    pageSize,
});

export const CargoCompanyDistributionAreas = (): React.JSX.Element => {
    const [isLoading, setIsLoading] = useState(false);
    const [isSearched, setIsSearched] = useState(false);
    const [searchDrawerOpen, setSearchDrawerOpen] = useState(false);
    const [gridData, setGridData] = useState<GridRow[]>([]);
    const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
    const [pageNumber, setPageNumber] = useState(0);
    const [totalCount, setTotalCount] = useState(0);
    const [lastSearchParams, setLastSearchParams] = useState<FilterParams | null>(null);

    const { t } = useTranslation();

    // Memoized function to fetch distribution areas
    const fetchDistributionAreas = useCallback(
        (payload: ApiPayload, onSuccess: (data: GridRow[], total: number, page: number) => void) => {
            setIsLoading(true);
            CargoCompanyDistributionAreasCommand.CargoDistributionAreaList(payload)
                .then((res: any) => {
                    const items = res?.items || [];
                    const transformedData = transformApiItemsToGridRows(items);
                    const total = res?.totalCount || 0;
                    onSuccess(transformedData, total, payload.page - 1);
                })
                .catch(() => {
                    Swal.fire({
                        icon: 'error',
                        title: t('general.anErrorOccured'),
                        confirmButtonText: t('general.ok'),
                    });
                })
                .finally(() => {
                    setIsLoading(false);
                });
        },
        []
    );

    const handleSearch = useCallback(
        (filterParams: FilterParams) => {
            setSearchDrawerOpen(false);
            setLastSearchParams(filterParams);

            const payload = buildApiPayload(filterParams, 1, DEFAULT_PAGE_SIZE);
            fetchDistributionAreas(payload, (data, total) => {
                setGridData(data);
                setTotalCount(total);
                setPageNumber(0);
                setPageSize(DEFAULT_PAGE_SIZE);
                setIsSearched(true);
            });
        },
        [fetchDistributionAreas]
    );

    const handlePageChange = useCallback(
        (newPage: number) => {
            if (!lastSearchParams) return;

            const payload = buildApiPayload(lastSearchParams, newPage + 1, pageSize);
            fetchDistributionAreas(payload, (data, total) => {
                setGridData(data);
                setTotalCount(total);
                setPageNumber(newPage);
            });
        },
        [lastSearchParams, pageSize, fetchDistributionAreas]
    );

    const handlePageSizeChange = useCallback(
        (newPageSize: number) => {
            if (!lastSearchParams) return;

            setPageSize(newPageSize);
            const payload = buildApiPayload(lastSearchParams, 1, newPageSize);
            fetchDistributionAreas(payload, (data, total) => {
                setGridData(data);
                setTotalCount(total);
                setPageNumber(0);
            });
        },
        [lastSearchParams, fetchDistributionAreas]
    );

    const openSearchDrawer = useCallback(() => {
        setSearchDrawerOpen(true);
    }, []);

    const closeSearchDrawer = useCallback(() => {
        setSearchDrawerOpen(false);
    }, []);

    const resetSearch = useCallback(() => {
        setIsSearched(false);
        setGridData([]);
        setLastSearchParams(null);
        setPageNumber(0);
        setTotalCount(0);
    }, []);

    const handleExportExcel = useCallback(() => {
        const excelRows = gridData.map((row) => ({
            shippingCompanyName: row.shippingCompanyName || '-',
            cityName: row.cityName || '-',
            countyName: row.countyName || '-',
            neighborhoodName: row.neighborhoodName || '-',
            distributionTypeName: row.distributionTypeName || '-',
            dayCount: row.dayCount || 0,
            weekDays: row.weekDays || '-',
            updatedDate: row.changedOn ? TimeHelper.formatDate(row.changedOn) : '-',
        }));

        const now = new Date();
        const dd = String(now.getDate()).padStart(2, '0');
        const mm = String(now.getMonth() + 1).padStart(2, '0');
        const yyyy = now.getFullYear();
        const fileName = `KargoFirmasiDagitimAlanlari_${dd}.${mm}.${yyyy}.xlsx`;

        downloadExcel({
            excelDownloadData: excelRows,
            excelDownloadFolderName: EXCEL_FOLDER_NAME,
            fileNameOverride: fileName,
        });
    }, [gridData]);

    const columnData = useMemo(
        () => [
            {
                field: 'shippingCompanyName',
                headerName: t('cargoCompanyDistributionAreas.columns.shippingCompanyName'),
                flex: 1,
                minWidth: 150,
                renderCell: (params: any) => <span>{params?.row?.shippingCompanyName ?? '-'}</span>,
            },
            {
                field: 'cityName',
                headerName: t('cargoCompanyDistributionAreas.columns.cityName'),
                flex: 1,
                minWidth: 120,
                renderCell: (params: any) => <span>{params?.row?.cityName ?? '-'}</span>,
            },
            {
                field: 'countyName',
                headerName: t('cargoCompanyDistributionAreas.columns.countyName'),
                flex: 1,
                minWidth: 120,
                renderCell: (params: any) => <span>{params?.row?.countyName ?? '-'}</span>,
            },
            {
                field: 'neighborhoodName',
                headerName: t('cargoCompanyDistributionAreas.columns.neighborhoodName'),
                flex: 1,
                minWidth: 150,
                renderCell: (params: any) => <span>{params?.row?.neighborhoodName ?? '-'}</span>,
            },
            {
                field: 'distributionTypeName',
                headerName: t('cargoCompanyDistributionAreas.columns.distributionTypeName'),
                flex: 1,
                minWidth: 150,
                renderCell: (params: any) => <span>{params?.row?.distributionTypeName ?? '-'}</span>,
            },
            {
                field: 'dayCount',
                headerName: t('cargoCompanyDistributionAreas.columns.dayCount'),
                width: 150,
                align: 'center',
                headerAlign: 'center',
                renderCell: (params: any) => <span>{params?.row?.dayCount ?? '-'}</span>,
            },
            {
                field: 'weekDays',
                headerName: t('cargoCompanyDistributionAreas.columns.weekDays'),
                flex: 1,
                minWidth: 200,
                renderCell: (params: any) => <span>{params?.row?.weekDays ?? '-'}</span>,
            },
            {
                field: 'changedOn',
                headerName: t('cargoCompanyDistributionAreas.columns.changedOn'),
                width: 160,
                align: 'center',
                headerAlign: 'center',
                renderCell: (params: any) => {
                    const dateValue = params?.row?.changedOn;
                    if (dateValue && dateValue !== '-' && dateValue !== '') {
                        return <span>{TimeHelper.formatDate(dateValue)}</span>;
                    }
                    return <span>-</span>;
                },
            },
        ],
        [t]
    );

    return (
        <SCUIContent className={`${CCDA.page} `}>
            <Paper
                component="div"
                className={CCDA.paper}
                sx={{
                    p: '24px',
                    paddingTop: '36px',
                    display: 'flex',
                    flexDirection: 'column',
                    width: '100%',
                }}
            >
                <SCUIBreadCrumb
                    title={t('cargoCompanyDistributionAreas.title')}
                    componentLink={PageUrl.cargoCompanyDistributionAreas.url}
                    onClick={resetSearch}
                />
                <SCUILoadingLinear display={isLoading} />

                {/* Search Drawer */}
                <SearchDrawer
                    isDrawerOpen={searchDrawerOpen}
                    handleDrawerClose={closeSearchDrawer}
                    handleSearch={handleSearch}
                />

                {isSearched ? (
                    <Box sx={{ marginTop: '24px' }}>
                        {/* Action Buttons */}
                        <Box
                            className={CCDA.toolbar}
                            sx={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginBottom: '12px' }}
                        >
                            <SCUIButton
                                text={t('general.exportToExcel')}
                                type="secondary"
                                sizeType="fixed"
                                style={{ height: '35px' }}
                                size={'m'}
                                buttonType="button"
                                onClick={handleExportExcel}
                                showStartIcon
                                startIcon={<FileDownloadIcon sx={{ color: '#0d6efd' }} />}
                                disabled={isLoading || gridData.length === 0}
                                className={ccdaCls(CCDA.input, CCDA.inputButton, CCDA.btnExportExcel)}
                            />
                            <SCUIButton
                                text={t('general.search')}
                                type="secondary"
                                sizeType="fixed"
                                style={{ height: '35px' }}
                                size={'m'}
                                buttonType="button"
                                onClick={openSearchDrawer}
                                showStartIcon
                                startIcon={<Search sx={{ color: '#0d6efd' }} />}
                                disabled={isLoading}
                                className={ccdaCls(CCDA.input, CCDA.inputButton, CCDA.btnOpenSearch)}
                            />
                        </Box>

                        <Box className={CCDA.datagrid}>
                            <SCUICustomDataGrid
                                paginationModeChange
                                onPageSizeChange={handlePageSizeChange}
                                pageSize={pageSize}
                                onPageChange={handlePageChange}
                                page={pageNumber}
                                getRowId={(row: any) => row?.id}
                                rows={gridData}
                                columns={columnData}
                                rowCount={totalCount}
                            />
                        </Box>
                    </Box>
                ) : (
                    <Box
                        className={CCDA.emptyState}
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}
                    >
                        <SCUINoRecordComponent img={airdropShipment} text={t('cargoCompanyDistributionAreas.search')} />
                        <SCUIButton
                            text={t('general.search')}
                            type="primary"
                            sizeType="fixed"
                            size={'m'}
                            showStartIcon
                            style={{ width: '120px', marginTop: '16px' }}
                            disabled={isLoading}
                            startIcon={
                                isLoading ? (
                                    <CircularProgress size={20} style={{ color: 'white' }} />
                                ) : (
                                    <Search style={{ color: 'white' }} />
                                )
                            }
                            onClick={openSearchDrawer}
                            className={ccdaCls(CCDA.input, CCDA.inputButton, CCDA.btnEmptySearch)}
                        />
                    </Box>
                )}
            </Paper>
        </SCUIContent>
    );
};
